import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from "firebase/firestore";

// --- Zoom Controls Component ---
function ZoomControls({ onZoomIn, onZoomOut }) {
  return (
    <div className="zoom-controls">
      <button onClick={onZoomOut} className="zoom-btn" title="Zoom Out">-</button>
      <button onClick={onZoomIn} className="zoom-btn" title="Zoom In">+</button>
    </div>
  );
}

// --- Start Screen Component ---
function StartScreen({ quizData, onStart, onZoomIn, onZoomOut }) {
  const { title, class: quizClass, subject, chapterTopic, unit, part, questions } = quizData;
  return (
    <div className="start-screen">
      <ZoomControls onZoomIn={onZoomIn} onZoomOut={onZoomOut} />
      <h1>{title}</h1>
      <div className="quiz-details">
        {quizClass && <p><strong>Class:</strong> {quizClass}</p>}
        {unit && <p><strong>Unit:</strong> {unit}</p>}
        {subject && <p><strong>Lesson:</strong> {subject}</p>}
        {chapterTopic && <p><strong>Topic:</strong> {chapterTopic}</p>}
        {part && <p><strong>Part:</strong> {part}</p>}
        <p><strong>Total Questions:</strong> {questions.length}</p>
      </div>
      <button onClick={onStart} className="start-btn">Start Quiz</button>
    </div>
  );
}

// --- Result Screen Component ---
// (यह कम्पोनेंट पिछले सुधार के बाद सही है, इसमें कोई बदलाव नहीं है)
function ResultScreen({ score, total, negativeMarks, correctAnswers, quizData, userAnswers, onZoomIn, onZoomOut, shuffledQuestions }) {
  return (
    <div className="result-screen">
      <ZoomControls onZoomIn={onZoomIn} onZoomOut={onZoomOut} />
      <h2>Quiz Completed!</h2>
      <h3>Your Score: {score} / {total}</h3>
      <p>Total Correct Answers: {correctAnswers}</p>
      <p>Total Negative Marks: -{negativeMarks}</p>
      <div className="explanations">
        <h3>Review Answers</h3>
        
        {/* यह अब 'shuffledQuestions' का उपयोग कर रहा है, जिसमें शफल किए गए विकल्प भी होंगे */}
        {shuffledQuestions.map((q, index) => {
          const userAnswer = userAnswers[index];
          const isCorrect = userAnswer === q.correctAnswer;
          const isSkipped = userAnswer === undefined || userAnswer === null;
          return (
            <div key={index} className="explanation-item">
              <strong style={{ whiteSpace: 'pre-wrap' }}>{index + 1}. {q.questionText}</strong>
              <ul>
                {/* 🔥 MISTAKE FIXED: 
                  q.options अब वही शफल की हुई लिस्ट है जो यूजर ने क्विज में देखी थी, 
                  क्योंकि हमने इसे 'shuffledQuestions' स्टेट में ही सेव कर दिया है।
                */}
                {q.options.map((option, optIndex) => {
                  let optionClass = '';
                  if (option === q.correctAnswer) optionClass = 'correct-option';
                  else if (option === userAnswer && !isCorrect) optionClass = 'wrong-user-option';
                  return (
                    <li key={optIndex} className={optionClass} style={{ whiteSpace: 'pre-wrap' }}>
                      {option}
                      {option === userAnswer && !isCorrect && !isSkipped && " (Your Answer - Wrong)"}
                      {option === userAnswer && isCorrect && " (Your Answer - Correct)"}
                      {isSkipped && option === q.correctAnswer && " (Correct Answer)"}
                    </li>
                  );
                })}
              </ul>
              {isSkipped && <p className="skipped-option" style={{ padding: '0.7em', borderRadius: '6px', margin: '0.5em 0', fontWeight: '600' }}>You did not attempt this question.</p>}
              <p className="exp-text" style={{ whiteSpace: 'pre-wrap' }}>Explanation: {q.explanation}</p>
            </div>
          );
        })}
      </div>
      <button onClick={() => window.location.reload()} className="start-btn">Retake Quiz</button>
    </div>
  );
}


// --- Main Quiz Player Component ---
function Quiz() {
  const { quizId } = useParams();
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [negativeMarks, setNegativeMarks] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1); 

  // Data fetching and event listeners
  useEffect(() => {
    let visibilityListener = null;
    let rightClickListener = event => event.preventDefault();
    const fetchQuiz = async () => {
      try {
        const docRef = doc(db, "quizzes", quizId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setQuizData(data);
          
          // --- 🔥 FIX 1 (Option Shuffle Logic) ---
          // 1. पहले प्रश्नों का क्रम तय करें (शफल या ओरिजिनल)
          let baseQuestions = data.questions;
          if (data.shuffleQuestions) {
            baseQuestions = [...data.questions].sort(() => Math.random() - 0.5);
          }

          // 2. अब जांचें कि क्या विकल्पों को भी शफल करने की जरूरत है
          if (data.shuffleOptions) {
            // हर प्रश्न (q) के लिए, उसके विकल्पों (q.options) को शफल करें
            const questionsWithOptionsShuffled = baseQuestions.map(q => {
              const shuffledOpts = [...q.options].sort(() => Math.random() - 0.5);
              // प्रश्न को शफल किए हुए विकल्पों के साथ लौटाएं
              return { ...q, options: shuffledOpts };
            });
            // 'shuffledQuestions' स्टेट में वह लिस्ट सेव करें जिसमें प्रश्न और उनके विकल्प दोनों शफल हो चुके हैं
            setShuffledQuestions(questionsWithOptionsShuffled);
          } else {
            // अगर विकल्प शफल नहीं करने हैं, तो सिर्फ प्रश्न-शफल वाली लिस्ट सेव करें
            setShuffledQuestions(baseQuestions);
          }
          // --- एंड ऑफ़ फिक्स 1 ---
          
          // Apply quiz settings only if necessary
          if (data.disableRightClick) document.addEventListener('contextmenu', rightClickListener);
          if (data.enableFullscreen) {
            const handleVisibilityChange = () => {
              if (document.visibilityState === 'hidden') {
                alert("You changed tabs! The quiz will now restart."); 
                window.location.reload();
              }
            };
            visibilityListener = handleVisibilityChange;
            document.addEventListener('visibilitychange', visibilityListener);
          }
        } else { setError("Quiz not found."); }
      } catch (err) { setError("Failed to load quiz."); console.error(err); }
      setLoading(false);
    };
    fetchQuiz();
    return () => {
      if (visibilityListener) document.removeEventListener('visibilitychange', visibilityListener);
      document.removeEventListener('contextmenu', rightClickListener);
    };
  }, [quizId]); // निर्भरता (dependency) सही है

  const startQuiz = () => {
    if (quizData.enableFullscreen) {
      document.documentElement.requestFullscreen().catch(err => { console.log(`Error: ${err.message}`); });
    }
    setQuizStarted(true);
    setSelectedOption(userAnswers[0] || null);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setUserAnswers({ ...userAnswers, [currentQuestionIndex]: option });
  };

  const calculateResults = useCallback(() => {
    let currentScore = 0, totalCorrect = 0, totalNegative = 0;
    const points = quizData.pointsPerQuestion || 1;
    const negative = quizData.negativePoints || 0;
    // यह लॉजिक पहले से ही 'shuffledQuestions' का उपयोग कर रहा था, जो सही है
    shuffledQuestions.forEach((question, index) => {
      const userAnswer = userAnswers[index];
      if (userAnswer === question.correctAnswer) {
        currentScore += points; totalCorrect += 1;
      } else if (userAnswer !== undefined && userAnswer !== null) {
        currentScore -= negative; totalNegative += negative;
      }
    });
    setScore(currentScore);
    setCorrectAnswers(totalCorrect);
    setNegativeMarks(totalNegative);
  }, [quizData, shuffledQuestions, userAnswers]);

  const handleNext = useCallback(() => {
    const nextQuestionIndex = currentQuestionIndex + 1;
    if (nextQuestionIndex < shuffledQuestions.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
      setSelectedOption(userAnswers[nextQuestionIndex] || null);
    } else {
      calculateResults();
      setQuizFinished(true);
    }
  }, [currentQuestionIndex, shuffledQuestions.length, userAnswers, calculateResults]);

  const handlePrevious = useCallback(() => {
    const prevQuestionIndex = currentQuestionIndex - 1;
    if (prevQuestionIndex >= 0) {
      setCurrentQuestionIndex(prevQuestionIndex);
      setSelectedOption(userAnswers[prevQuestionIndex] || null);
    }
  }, [currentQuestionIndex, userAnswers]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!quizStarted || quizFinished) return;
      if (event.key === 'ArrowRight') {
        if (currentQuestionIndex < shuffledQuestions.length) handleNext();
      } else if (event.key === 'ArrowLeft') {
        if (currentQuestionIndex > 0) handlePrevious();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [quizStarted, quizFinished, currentQuestionIndex, shuffledQuestions.length, handleNext, handlePrevious]);

  
  // --- 🔥 FIX 2 (सरल किया गया) ---
  // Option shuffling on question change
  useEffect(() => {
    if (quizStarted && !quizFinished && shuffledQuestions.length > 0) {
      const currentQuestion = shuffledQuestions[currentQuestionIndex];
      // 'shuffledQuestions' में विकल्प पहले से ही शफल (या नहीं) हैं।
      // बस उन्हें 'shuffledOptions' स्टेट में सेट करें ताकि वे स्क्रीन पर दिखें।
      setShuffledOptions(currentQuestion.options);
    }
  }, [currentQuestionIndex, quizStarted, quizFinished, shuffledQuestions]); // 'quizData' को निर्भरता से हटा दिया गया है

  const handleZoomIn = () => {
    setZoomLevel(prevZoom => Math.min(prevZoom + 0.1, 1.5));
  };
  const handleZoomOut = () => {
    setZoomLevel(prevZoom => Math.max(prevZoom - 0.1, 0.8));
  };

  const isQuizActive = quizStarted && !quizFinished;
  const isStartScreen = !quizStarted; 
  const isSmallCard = isStartScreen; 

  const appStyle = {
    fontSize: `${zoomLevel}rem`,
    maxWidth: isSmallCard ? '850px' : '100%',
    margin: isSmallCard ? '20px auto' : '0', 
    borderRadius: isSmallCard ? '12px' : '0',
    minHeight: '100vh',
  };

  if (loading) {
    return <div className="App" style={appStyle}><div className="loading">Loading Quiz...</div></div>;
  }
  if (error) {
    return <div className="App" style={appStyle}><div className="loading">{error}</div></div>;
  }

  // Result Screen (Full Width)
  if (quizFinished) {
    return (
      <div className="App" style={appStyle}>
        <ResultScreen 
          score={score}
          total={shuffledQuestions.length * (quizData.pointsPerQuestion || 1)}
          negativeMarks={negativeMarks}
          correctAnswers={correctAnswers}
          quizData={quizData} // यह अभी भी पास किया जा रहा है (भविष्य के उपयोग के लिए)
          userAnswers={userAnswers}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          shuffledQuestions={shuffledQuestions} // यह 'shuffledQuestions' लिस्ट है जिसमें शफल किए गए विकल्प भी हैं
        />
      </div>
    );
  }

  // Start Screen (Small/Centered)
  if (!quizStarted) {
    return (
      <div className="App" style={appStyle}>
        <StartScreen 
          quizData={quizData} 
          onStart={startQuiz} 
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />
      </div>
    );
  }

  // Active Quiz (Full Width)
  return (
    <div className="App" style={appStyle}>
      <div className="quiz-header">
        <h2>{quizData.title}</h2>
        <div className="header-right-side">
          <ZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
          <div className="quiz-progress">
            Question {currentQuestionIndex + 1} of {shuffledQuestions.length}
          </div>
        </div>
      </div>
      
      {shuffledQuestions.length > 0 && (
        <>
          <h3 style={{ whiteSpace: 'pre-wrap' }}>
            {shuffledQuestions[currentQuestionIndex].questionText}
          </h3>
          
          <ul>
            {/* यह 'shuffledOptions' स्टेट का उपयोग करता है, जो अब सही क्रम दिखाता है */}
            {shuffledOptions.map((option, index) => (
              <li 
                key={index}
                onClick={() => handleOptionClick(option)}
                className={selectedOption === option ? 'selected' : ''}
              >
                <span style={{ whiteSpace: 'pre-wrap' }}>{option}</span>
              </li>
            ))}
          </ul>

          <div className="navigation-buttons">
            <button 
              onClick={handlePrevious} 
              className="prev-btn"
              disabled={currentQuestionIndex === 0}
            >
              &laquo; Previous
            </button>
            <button onClick={handleNext}>
              {currentQuestionIndex === shuffledQuestions.length - 1 ? "Finish Quiz" : "Next Question"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Quiz;